import { createFileRoute, Outlet, redirect, useNavigate, useMatches } from "@tanstack/react-router";
import { Layout, Typography, Button, Flex, theme } from "antd";
import { LogoutOutlined, CrownOutlined, MenuOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/configs/supabase/config";
import { Provider_Shell, useShell_Nav } from "@/providers/Provider_Shell";

interface RouteContext {
  userId: string;
  userEmail: string;
  userName: string;
}

export const Route = createFileRoute("/_protected")({
  beforeLoad: async ({ location }): Promise<RouteContext> => {
    const sb_Auth_GetSession = await supabase.auth.getSession();
    const session = sb_Auth_GetSession.data.session;

    if (!session) {
      const redirectPath = `${location.pathname}${location.search ? `?${new URLSearchParams(location.search as Record<string, string>).toString()}` : ""}`;
      throw redirect({ to: "/login", search: { redirect: redirectPath } });
    }

    const sb_Auth_GetUser = await supabase.auth.getUser();
    const user = sb_Auth_GetUser.data.user;

    return {
      userId: user?.id ?? "",
      userEmail: user?.email ?? "",
      userName: user?.user_metadata?.full_name ?? user?.email ?? "",
    };
  },
  component: ProtectedLayout,
});

function ProtectedLayout() {
  return (
    <Provider_Shell>
      <ProtectedShell />
    </Provider_Shell>
  );
}

function ProtectedShell() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { userId, userEmail, userName } = Route.useRouteContext();
  const nav = useShell_Nav();
  const matches = useMatches();
  const inFlow = matches.some((m) => m.routeId === "/_protected/$organizationId/$flowId");

  const qProfile = useQuery({
    queryKey: ["profiles", "is_god_mode", userId],
    queryFn: async () => {
      const sb_FromProfiles_Select = await supabase
        .from("profiles")
        .select("is_god_mode")
        .eq("id", userId)
        .single();
      if (sb_FromProfiles_Select.error) return false;
      return sb_FromProfiles_Select.data.is_god_mode ?? false;
    },
    enabled: !!userId,
  });

  const isAdmin = qProfile.data === true;

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/login" });
  };

  return (
    <Layout style={{ minHeight: "100dvh" }}>
      <Layout.Header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          height: 48,
          lineHeight: "48px",
          padding: `0 ${token.paddingLG}px`,
          background: token.colorBgContainer,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Flex align="center" gap={token.marginSM}>
          {inFlow && (
            <Button
              type="text"
              size="small"
              icon={<MenuOutlined />}
              aria-label="Toggle navigation"
              onClick={() => (nav.isMobile ? nav.setMobileOpen(!nav.mobileOpen) : nav.toggleCollapsed())}
            />
          )}
          <Typography.Text strong style={{ cursor: "pointer" }} onClick={() => navigate({ to: "/" })}>
            Insurance Hub
          </Typography.Text>
        </Flex>
        <Flex align="center" gap={nav.isMobile ? token.marginXS : token.marginMD}>
          {isAdmin && (
            <Button
              type="text"
              icon={<CrownOutlined />}
              size="small"
              aria-label="God Mode"
              onClick={() => navigate({ to: "/god-mode" })}
            >
              {nav.isMobile ? undefined : "God Mode"}
            </Button>
          )}
          {!nav.isMobile && <Typography.Text type="secondary">{userName || userEmail}</Typography.Text>}
          <Button
            type="text"
            icon={<LogoutOutlined />}
            size="small"
            aria-label="Logout"
            onClick={handleLogout}
          >
            {nav.isMobile ? undefined : "Logout"}
          </Button>
        </Flex>
      </Layout.Header>
      <Layout.Content>
        <Outlet />
      </Layout.Content>
    </Layout>
  );
}
